import { IntegrationType } from '@principle-theorem/integrations';
import {
  ServiceTypeModality,
  TemplateScope,
  type IEvent,
  type IInvoice,
  type IReferral,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  ISO_TIME_FORMAT,
  MockGenerator,
  Region,
  toTimestamp,
} from '@principle-theorem/shared';
import { MockDocRef, MockWithRef } from '@principle-theorem/testing';
import * as moment from 'moment-timezone';
import { AppointmentMock } from '../models/appointment/appointment.mock';
import { TreatmentPlanMock } from '../models/clinical-charting/treatment/treatment-plan.mock';
import { MockCandidateCalendarEvent } from '../models/event/calendar-event';
import { EventMock } from '../models/event/event.mock';
import { InvoiceMock } from '../models/invoice/invoice.mock';
import { PatientMock } from '../models/patient/patient.mock';
import { PracticeMock } from '../models/practice/practice.mock';
import { PractitionerMock } from '../models/staffer/practitioner.mock';
import { ReferralMock } from '../models/referral/referral.mock';
import { AppointmentContextBuilder } from './contexts/appointment-context';
import { GapContextBuilder } from './contexts/gap-context';
import { InvoiceContextBuilder } from './contexts/invoice-context';
import { PatientContextBuilder } from './contexts/patient-context';
import { ReferralContextBuilder } from './contexts/referral-context';
import { TreatmentPlanContextBuilder } from './contexts/treatment-plan-context';
import {
  IContextResolver,
  ScopeContextProvider,
  ScopeContextResolver,
} from './template-context-resolver';

const mockPatient = MockWithRef(
  MockGenerator.generate(PatientMock, {
    name: 'Allan (Al) Sutherland',
    email: 'allan.sutherland@gmail.com',
    address: '11 York St, Berrydale, NSW 2535',
    dateOfBirth: '1980-01-16',
  })
);
const mockPractice = MockWithRef(
  MockGenerator.generate(PracticeMock, {
    name: 'North Sydney Smiles',
    email: 'info@northsydneysmiles.au',
    phone: '02 1234 5678',
  })
);
const mockAppointment = MockWithRef(
  MockGenerator.generate(AppointmentMock, {
    // Explicit generics required: `event?: IEvent` on the outer overrides is
    // optional, so contextual inference gives the inner call `Interface = IEvent
    // | undefined`, which collapses `Partial<NoInfer<Interface>>` to `undefined`.
    event: MockGenerator.generate<EventMock, IEvent>(EventMock, {
      from: toTimestamp(
        moment
          .tz('10:00:00', ISO_TIME_FORMAT, mockPractice.settings.timezone)
          .add(1, 'week')
      ),
      to: toTimestamp(
        moment
          .tz('12:00:00', ISO_TIME_FORMAT, mockPractice.settings.timezone)
          .add(1, 'week')
      ),
    }),
    treatmentPlan: {
      name: 'Crown - PFM / Veneered',
      ref: MockDocRef<ITreatmentPlan>(),
      treatmentStep: {
        name: 'Crown Preparation',
        duration: 120,
        ref: MockDocRef<ITreatmentStep>(),
        display: {},
      },
    },
  })
);
const mockPractitioner = MockGenerator.generate(PractitionerMock, {
  providerDetails: [
    {
      practiceRef: mockPractice.ref,
      providerNumber: '3222411A',
      providerModality: ServiceTypeModality.GeneralDentist,
    },
  ],
});
const mockGapCandidate = MockCandidateCalendarEvent();

// Explicit generics required: `IInvoice` is a discriminated union.
// `Properties<IInvoice>` = `Required<Pick<IInvoice, keyof IInvoice>>`, and
// `keyof (A | B)` collapses to shared-only keys — dropping discriminant fields
// like `cancelledAt`/`cancellation` that only exist on some union members.
const mockInvoice = MockGenerator.generate<InvoiceMock, IInvoice>(InvoiceMock);
const mockViewInvoiceLink = 'https://app.principle.dental/invoice/9vbv20';
const mockGapOfferLink = 'https://app.principle.dental/reschedule/9sd823';
const mockMedicalHistoryLink = 'https://app.principle.dental/history/29cg82';
const mockPatientFormsLink = 'https://app.principle.dental/history/65xa16';
const mockConfirmationLink = 'https://app.principle.dental/confirm/a9g2jj';
const mockBookingLink = 'https://app.principle.dental/book/a9g2jj';
const mockUnsubscribeLink = 'https://app.principle.dental/unubscribe/a9g2jj';
const mockReferrer = {
  name: 'Melanie Green',
  email: 'melanie.green@gmail.com',
  address: '742 Evergreen Terrace, Springfield, NSW',
  jobTitle: 'Solicitor',
};
const mockTreatmentPlan = MockGenerator.generate(TreatmentPlanMock);

const mockReferral = MockWithRef<IReferral>({
  ...MockGenerator.generate(ReferralMock),
  uid: 'mock-referral-uid',
  patient: {
    name: mockPatient.name,
    ref: mockPatient.ref,
  },
  practice: {
    name: mockPractice.name,
    ref: mockPractice.ref,
  },
  stafferDetails: {
    name: 'North Sydney Smiles',
    email: 'info@northsydneysmiles.au',
    phone: '02 1234 5678',
  },
  patientDetails: {
    name: mockPatient.name,
    dateOfBirth: mockPatient.dateOfBirth ?? '',
    includePhone: false,
    includeEmail: false,
    includeAddress: false,
  },
});

export const MOCK_PATIENT_RESOLVER = new ScopeContextProvider(
  TemplateScope.Patient,
  new PatientContextBuilder({
    patient: mockPatient,
    practice: mockPractice,
    patientForms: [],
    medicalHistoryLink: mockMedicalHistoryLink,
    patientFormsLink: mockPatientFormsLink,
    appointmentBookingLink: mockBookingLink,
    unsubscribeLink: mockUnsubscribeLink,
    referrer: mockReferrer,
    smsIntegration: IntegrationType.Twilio,
    region: Region.Australia,
  })
);

export const MOCK_APPOINTMENT_RESOLVER = new ScopeContextProvider(
  TemplateScope.Appointment,
  new AppointmentContextBuilder({
    appointment: mockAppointment,
    practitioner: mockPractitioner,
    practice: mockPractice,
    patient: mockPatient,
    patientForms: [],
    smsIntegration: IntegrationType.Twilio,
    confirmLink: mockConfirmationLink,
    medicalHistoryLink: mockMedicalHistoryLink,
    patientFormsLink: mockPatientFormsLink,
    appointmentBookingLink: mockBookingLink,
    unsubscribeLink: mockUnsubscribeLink,
    referrer: mockReferrer,
    region: Region.Australia,
  })
);

export const MOCK_INVOICE_RESOLVER = new ScopeContextProvider(
  TemplateScope.Invoice,
  new InvoiceContextBuilder({
    invoice: mockInvoice,
    invoiceLink: mockViewInvoiceLink,
    patient: mockPatient,
    practice: mockPractice,
    transactions: [],
    patientForms: [],
    medicalHistoryLink: mockMedicalHistoryLink,
    patientFormsLink: mockPatientFormsLink,
    appointmentBookingLink: mockBookingLink,
    unsubscribeLink: mockUnsubscribeLink,
    smsIntegration: IntegrationType.Twilio,
    region: Region.Australia,
  })
);

export const MOCK_GAP_RESOLVER = new ScopeContextProvider(
  TemplateScope.GapOffer,
  new GapContextBuilder({
    gapCandidate: mockGapCandidate,
    gapOfferConfirmUrl: mockGapOfferLink,
    patient: mockPatient,
    medicalHistoryLink: mockMedicalHistoryLink,
    referrer: mockReferrer,
    appointment: mockAppointment,
    practitioner: mockPractitioner,
    practice: mockPractice,
    confirmLink: mockConfirmationLink,
    appointmentBookingLink: mockBookingLink,
    unsubscribeLink: mockUnsubscribeLink,
    region: Region.Australia,
    patientForms: [],
    patientFormsLink: mockPatientFormsLink,
  })
);

export const MOCK_TREATMENT_PLAN_RESOLVER = new ScopeContextProvider(
  TemplateScope.TreatmentPlan,
  new TreatmentPlanContextBuilder({
    treatmentPlan: mockTreatmentPlan,
    patient: mockPatient,
    practice: mockPractice,
    patientForms: [],
    medicalHistoryLink: mockMedicalHistoryLink,
    patientFormsLink: mockPatientFormsLink,
    appointmentBookingLink: mockBookingLink,
    unsubscribeLink: mockUnsubscribeLink,
    smsIntegration: IntegrationType.Twilio,
    region: Region.Australia,
  })
);

export const MOCK_EMPTY_CONTEXT_PROVIDER = new ScopeContextProvider(
  TemplateScope.None,
  {
    build: () => ({}),
  }
);

export const MOCK_REFERRAL_RESOLVER = new ScopeContextProvider(
  TemplateScope.Referral,
  new ReferralContextBuilder({
    referral: mockReferral,
    referrerAddress: '123 Medical Centre, Sydney NSW 2000',
    recipientAddress: '456 Specialist Clinic, Melbourne VIC 3000',
    patient: mockPatient,
    practice: mockPractice,
    patientForms: [],
    medicalHistoryLink: mockMedicalHistoryLink,
    patientFormsLink: mockPatientFormsLink,
    appointmentBookingLink: mockBookingLink,
    unsubscribeLink: mockUnsubscribeLink,
    referrer: mockReferrer,
    smsIntegration: IntegrationType.Twilio,
    region: Region.Australia,
  })
);

export const MOCK_ALL_RESOLVER: IContextResolver = new ScopeContextResolver([
  MOCK_PATIENT_RESOLVER,
  MOCK_APPOINTMENT_RESOLVER,
  MOCK_INVOICE_RESOLVER,
  MOCK_GAP_RESOLVER,
  MOCK_TREATMENT_PLAN_RESOLVER,
  MOCK_REFERRAL_RESOLVER,
  MOCK_EMPTY_CONTEXT_PROVIDER,
]);
